/**
 * Modals for the clinical-admin "eRx on file" card:
 *   - ErxDetailModal  — full field dump for a DoseSpot e-prescription
 *                       (Healthie Prescription node).
 *   - PdfPreviewModal — inline preview of a rendered PDF script. Mints a
 *                       fresh signed URL on open so the link is never stale.
 */
import { useEffect, useState, type ReactNode } from 'react';

import { fetchPrescriptionPdfUrl } from '@/lib/api/clinicalAdminHooks';
import type { ErxEntry } from '@/types/clinicalAdmin';

function Backdrop({
  onClose,
  children,
}: {
  onClose: () => void;
  children: ReactNode;
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center p-24 bg-ink/60 backdrop-blur-sm overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="bg-white border border-default shadow-xl w-full max-w-[920px] flex flex-col gap-16 p-15 my-24"
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
}

function Header({
  title,
  subtitle,
  onClose,
}: {
  title: string;
  subtitle?: string;
  onClose: () => void;
}) {
  return (
    <div className="flex items-start justify-between gap-12">
      <div className="flex flex-col gap-4">
        <h2 className="type-heading-lg">{title}</h2>
        {subtitle ? (
          <p className="type-body-sm text-ink-muted">{subtitle}</p>
        ) : null}
      </div>
      <button
        type="button"
        className="btn-ghost btn-sm"
        onClick={onClose}
        aria-label="Close"
      >
        Close
      </button>
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-2 border-b border-line-subtle pb-8">
      <span className="type-label-sm text-ink-muted uppercase tracking-wide">
        {label}
      </span>
      <span className="type-body-sm break-words">{value}</span>
    </div>
  );
}

function fmtValue(v: unknown): string {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  if (typeof v === 'object') {
    const o = v as Record<string, unknown>;
    if ('name' in o) return String(o.name ?? '—'); // e.g. pharmacy { id, name }
    return JSON.stringify(v);
  }
  return String(v);
}

const DOSESPOT_FIELDS: { key: string; label: string }[] = [
  { key: 'status', label: 'Status' },
  { key: 'normalized_status', label: 'Normalized status' },
  { key: 'dosage', label: 'Dosage' },
  { key: 'quantity', label: 'Quantity' },
  { key: 'refills', label: 'Refills' },
  { key: 'date_written', label: 'Date written' },
  { key: 'pharmacy', label: 'Pharmacy' },
  { key: 'ndc', label: 'NDC' },
  { key: 'rxcui', label: 'RxCUI' },
  { key: 'prescriber_id', label: 'Prescriber ID' },
  { key: 'id', label: 'Healthie ID' },
];

export function ErxDetailModal({
  entry,
  onClose,
}: {
  entry: ErxEntry;
  onClose: () => void;
}) {
  const detail = entry.detail ?? {};
  return (
    <Backdrop onClose={onClose}>
      <Header
        title={entry.name ?? 'Prescription'}
        subtitle="DoseSpot e-prescription — transmitted via Healthie/DoseSpot"
        onClose={onClose}
      />
      <div className="flex flex-col gap-12">
        <Field label="Directions" value={entry.directions ?? '—'} />
        {DOSESPOT_FIELDS.map((f) => (
          <Field key={f.key} label={f.label} value={fmtValue(detail[f.key])} />
        ))}
      </div>
    </Backdrop>
  );
}

export function PdfPreviewModal({
  patientId,
  entry,
  onClose,
}: {
  patientId: string;
  entry: ErxEntry;
  onClose: () => void;
}) {
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const pdfId = entry.prescription_pdf_id;
    if (!pdfId) {
      setError('No PDF has been generated for this medication record yet.');
      setLoading(false);
      return;
    }
    setLoading(true);
    fetchPrescriptionPdfUrl(patientId, pdfId)
      .then((u) => {
        if (cancelled) return;
        if (!u) setError('Could not generate a preview link for this PDF.');
        setUrl(u);
        setLoading(false);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : 'Could not load the PDF.');
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [patientId, entry.prescription_pdf_id]);

  const subtitle =
    [
      entry.partner_key ? `Pharmacy: ${entry.partner_key}` : null,
      entry.signed_by_name ? `Signed by ${entry.signed_by_name}` : null,
    ]
      .filter(Boolean)
      .join('  ·  ') || 'Rendered PDF script';

  return (
    <Backdrop onClose={onClose}>
      <Header
        title={entry.name ?? 'PDF script'}
        subtitle={subtitle}
        onClose={onClose}
      />
      {loading ? (
        <div className="px-15 py-30 text-center type-label-sm text-ink-muted">
          Loading PDF…
        </div>
      ) : error ? (
        <div className="px-15 py-30 text-center type-label-sm text-danger">
          {error}
        </div>
      ) : url ? (
        <div className="flex flex-col gap-12">
          <object
            data={url}
            type="application/pdf"
            className="w-full h-[72vh] border border-line bg-sunken"
          >
            <iframe
              src={url}
              title="Rx PDF preview"
              className="w-full h-[72vh] border border-line"
            />
          </object>
          <a
            href={url}
            target="_blank"
            rel="noreferrer"
            className="type-label-sm text-accent underline self-start"
          >
            Open in new tab ↗
          </a>
        </div>
      ) : null}
    </Backdrop>
  );
}
